import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/plugins/builder/templates/monthly-pdf-report/generator.tsx
// UI-only: builds a printable one-page report and opens the browser print dialog.
// Save-as-PDF is native to every browser, so we don't need to bundle a PDF lib.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { useReviews, useToast } from '../../../sdk';
import { Button } from '../../../../ui/Button';
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return _jsx(MonthlyReport, { name: ctx.instanceName, cfg: cfg });
};
function MonthlyReport({ name, cfg }) {
    const { reviews } = useReviews({ limit: 200 });
    const toast = useToast();
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const rows = (reviews ?? []).filter(r => (r.posted_at ?? '') >= monthStart);
    const count = rows.length;
    const avg = count ? (rows.reduce((s, r) => s + (r.rating ?? 0), 0) / count).toFixed(2) : '—';
    const fives = rows.filter(r => (r.rating ?? 0) === 5);
    const ones = rows.filter(r => (r.rating ?? 0) === 1);
    return (_jsxs("main", { id: "main", className: "mx-auto max-w-3xl px-6 py-10 print:px-0 print:py-0", children: [_jsxs("div", { className: "flex items-center justify-between print:hidden", children: [_jsx("h1", { className: "text-2xl font-semibold", children: name }), _jsx(Button, { onClick: () => { window.print(); toast.info('Use "Save as PDF" in the print dialog.'); }, children: "Download PDF" })] }), _jsxs("section", { className: "mt-6 rounded-2xl bg-white p-8 ring-1 ring-slate-200 print:rounded-none print:ring-0 print:shadow-none", children: [_jsx("h2", { className: "text-xl font-semibold", children: cfg.title }), _jsx("p", { className: "text-xs text-slate-500", children: now.toLocaleDateString() }), _jsxs("dl", { className: "mt-6 grid grid-cols-3 gap-4", children: [_jsxs("div", { children: [_jsx("dt", { className: "text-xs text-slate-500", children: "New reviews" }), _jsx("dd", { className: "text-2xl font-semibold", children: count })] }), _jsxs("div", { children: [_jsx("dt", { className: "text-xs text-slate-500", children: "Average rating" }), _jsx("dd", { className: "text-2xl font-semibold", children: avg })] }), _jsxs("div", { children: [_jsx("dt", { className: "text-xs text-slate-500", children: "5\u2605 / 1\u2605 split" }), _jsxs("dd", { className: "text-2xl font-semibold", children: [fives.length, "/", ones.length] })] })] }), cfg.showQuotes && fives.length > 0 && (_jsxs("div", { className: "mt-6", children: [_jsx("p", { className: "text-sm font-medium", children: "Highlights" }), _jsx("ul", { className: "mt-2 space-y-2", children: fives.slice(0, 3).map(r => (_jsxs("li", { className: "rounded-lg bg-slate-50 p-3 text-sm", children: ["\"", (r.body ?? '').slice(0, 220), "\" \u2014 ", r.author_name ?? 'a customer'] }, r.id))) })] }))] })] }));
}
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `One-page printable report: "${cfg.title}".`,
        category: 'reporting', icon: 'FileText',
        permissions: ['read:reviews', 'ui:toast'],
        entry: 'ui',
        config: meta.configSchema,
        defaultConfig: { title: cfg.title, showQuotes: cfg.showQuotes },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useReviews } from '../sdk';
import { Button } from '../../ui/Button';
export default function ${cls(params.instanceId)}() {
  const { reviews } = useReviews({ limit: 200 });
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
  const rows = (reviews ?? []).filter(r => (r.posted_at ?? '') >= monthStart);
  const count = rows.length;
  const avg = count ? (rows.reduce((s, r) => s + (r.rating ?? 0), 0) / count).toFixed(2) : '—';
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10 print:px-0 print:py-0">
      <div className="print:hidden mb-4"><Button onClick={() => window.print()}>Download PDF</Button></div>
      <h1 className="text-xl font-semibold">${esc(cfg.title)}</h1>
      <p>{count} reviews · avg {avg}</p>
      ${cfg.showQuotes ? `<ul className="mt-4 space-y-2">{rows.filter(r => (r.rating ?? 0) === 5).slice(0, 3).map(r => (<li key={r.id} className="rounded bg-slate-50 p-3 text-sm">"{(r.body ?? '').slice(0, 200)}"</li>))}</ul>` : ''}
    </main>
  );
}
`;
    return assemble({ manifest, pluginTsx, hooksTs: null,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `Generates a printable report of this month's reviews.`,
            configSummary: [['title', cfg.title], ['showQuotes', String(cfg.showQuotes)]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => cfg.title || 'Monthly Review Report',
    describe: (cfg) => `Build a printable one-page PDF titled "${cfg.title}" showing this month's review stats${cfg.showQuotes ? ' with 5★ quote highlights' : ''}.`,
    generate, runtimeUi: RuntimeUi,
    clickCount: meta.clickCount,
};
export default template;
